import React, { Fragment, useEffect, useState } from 'react';
import { arrayOf, bool } from 'prop-types';
import { useHistory, useLocation, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import {connect, useSelector} from 'react-redux';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import { map, path, pathOr } from 'ramda';

import { getListingsById } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import { propTypes } from '../../util/types';
import { isNilOrEmpty } from '../../util/validators';
import { TopbarContainer } from '../../containers';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  IconSpinner,
  ListingCard, PrimaryButton, SecondaryButton,
} from '../../components';

import css from './AvailabilitiesPage.module.css';
import config from '../../config';
import { createResourceLocatorString } from '../../util/routes';
import { updateNextAvailability } from './AvailabilitiesPage.duck';
import { parse } from '../../util/urlHelpers';
import routeConfiguration from '../../routeConfiguration';
import {moment} from "../../util/dates";
import { mailContact } from '../../marketplace-custom-config';

const { UUID } = sdkTypes;

const AvailabilitiesPage = (props) => {
  const {
    listings,
    fetchFavoritesInProgress,
    fetchFavoritesError,
    fetchFavoritesSuccess,
    scrollingDisabled,
    updateNextAvailability,
    intl,
  } = props;
  const routes = routeConfiguration();
  const listingsAreLoaded = !fetchFavoritesInProgress && fetchFavoritesSuccess;
  const mailContact = config.custom.mailContact;
  const currentUserHasListings = useSelector(state => state.user.currentUserHasListings);
  const currentUserListingRefs = useSelector(state => state.user.currentUserListingRefs);
  const currentListingId = path([0,'id', 'uuid'], currentUserListingRefs);
  const getNextWeekRange = () => {
    const startOfWeek = moment().add(1, "week").startOf("isoWeek");
    const endOfWeek = moment().add(1, "week").endOf("isoWeek");

    return `${startOfWeek.format("dddd DD/MM/YY")} - ${endOfWeek.format("dddd DD/MM/YY")}`;
  };
  const AVAILABLE = 'AVAILABLE';
  const AVAILABLE_ONLY_FOR_EVENTS = 'AVAILABLE_ONLY_FOR_EVENTS';
  const NOT_AVAILABLE = 'NOT_AVAILABLE';
  const AVAILABLE_PARTIALLY = 'AVAILABLE_PARTIALLY';
  const messages = {
    [AVAILABLE]: intl.formatMessage({ id: 'AvailabilitiesPage.yesMessage' }),
    [AVAILABLE_ONLY_FOR_EVENTS]: intl.formatMessage({ id: 'AvailabilitiesPage.yesButOnlyEventsMessage' }),
    [NOT_AVAILABLE]: intl.formatMessage({ id: 'AvailabilitiesPage.noMessage' }, { mailContact }),
    [AVAILABLE_PARTIALLY]: '',
  };
  const [hasSelected, setHasSelected] = useState(null);
  const location = useLocation();
  const searchParams = parse(location.search) ?? {};
  const history = useHistory();
  const { nextAvailability } = searchParams;

  useEffect(() => {
    setHasSelected(nextAvailability);
    if (!nextAvailability) return;

    console.log('Detected choice from query:', nextAvailability);
    updateNextAvailability(nextAvailability);
    if (nextAvailability !== NOT_AVAILABLE && currentListingId) {
      history.push(createResourceLocatorString('CalendarPage', routes, { id: currentListingId }, {fromAvailabilitiesPage: true}));
    }
  }, [nextAvailability, currentListingId]);

  const queryError = (
    <h2 className={css.error}>
      <FormattedMessage id="ManageListingsPage.queryError" />
    </h2>
  );
  const noResults =
    listingsAreLoaded && isNilOrEmpty(listings) ? (
      <div className={css.rootNoResults}>
        <div className={css.contentNoResults}>
          <h1 className={css.headingNoResults}>
            <FormattedMessage id="FavoritesPage.noResultsHeading" />
          </h1>
          <p className={css.descriptionNoResults}>
            <FormattedMessage id="FavoritesPage.noResultsDescription" />
          </p>
        </div>
      </div>
    ) : null;
  const title = intl.formatMessage({ id: 'AvailabilitiesPage.title' });
  const question = intl.formatMessage({ id: 'AvailabilitiesPage.question' });

  const handleSelectChoice = (nextAvailability) => {
    history.push(createResourceLocatorString('AvailabilitiesPage', routes, {}, {nextAvailability}))
  };
  if (!currentUserHasListings) return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="AvailabilitiesPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.layoutWrapperMain}>
          <div className={css.header}>
            <h1 className={css.heading}>{title}</h1>
          </div>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  )

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="AvailabilitiesPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.layoutWrapperMain}>
          <div className={css.header}>
            <h1 className={css.heading}>{title}</h1>
          </div>
          <div className={css.content}>
            {fetchFavoritesInProgress && isNilOrEmpty(listings) && <IconSpinner />}
            {fetchFavoritesError && queryError}
            <div className={css.cardContainer}>
              {hasSelected ? (
                <p>{messages[hasSelected]}</p>
              ) : (
                <>
                  <h1 className={css.question}>{question}</h1>
                  <div className={css.weekIndicator}>{getNextWeekRange()}</div>
                  <PrimaryButton onClick={() => handleSelectChoice(AVAILABLE)}
                                 className={css.yesButton}>
                    <FormattedMessage id="AvailabilitiesPage.yes" />
                  </PrimaryButton>
                  <PrimaryButton onClick={() => handleSelectChoice(AVAILABLE_ONLY_FOR_EVENTS)}
                                 className={css.yesButton}>
                    <FormattedMessage id="AvailabilitiesPage.yesButOnlyEvents" />
                  </PrimaryButton>
                  {/*<SecondaryButton onClick={() => handleSelectChoice(AVAILABLE_PARTIALLY)}*/}
                  {/*                 className={css.partlyButton}>*/}
                  {/*  <FormattedMessage id="AvailabilitiesPage.partly" />*/}
                  {/*</SecondaryButton>*/}
                  <SecondaryButton onClick={() => handleSelectChoice(AVAILABLE_PARTIALLY)}
                                   className={css.partlyButton}>
                    <FormattedMessage id="AvailabilitiesPage.noButKeepOpen" />
                  </SecondaryButton>
                  <PrimaryButton onClick={() => handleSelectChoice(NOT_AVAILABLE)}
                                 className={css.noButton}>
                    <FormattedMessage id="AvailabilitiesPage.no" />
                  </PrimaryButton>
                </>
              )}
            </div>
            {!hasSelected && (
              <div className={css.cautionMessage}>
                <FormattedMessage id="AvailabilitiesPage.noAnswerWarning" />
              </div>
            )}
          </div>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  );
};


AvailabilitiesPage.defaultProps = {
  listings: [],
  fetchFavoritesError: null,
};

AvailabilitiesPage.propTypes = {
  fetchFavoritesError: propTypes.error,
  fetchFavoritesInProgress: bool.isRequired,
  fetchFavoritesSuccess: bool.isRequired,
  intl: intlShape.isRequired,
  listings: arrayOf(propTypes.listing),
  scrollingDisabled: bool.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    fetchFavoritesInProgress,
    fetchFavoritesError,
    fetchFavoritesSuccess,
  } = state.FavoritesPage;
  const myFavorites = pathOr([], ['attributes', 'profile', 'protectedData', 'myFavorites'], currentUser);
  const listings = getListingsById(state, map((fav) => new UUID(fav), myFavorites));

  return {
    currentPageResultIds,
    listings,
    fetchFavoritesInProgress,
    fetchFavoritesError,
    fetchFavoritesSuccess,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = ({
  updateNextAvailability,
});

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  injectIntl,
)(AvailabilitiesPage);
